/**
 * The phone menu: a hamburger in the top bar and a panel that drops down from
 * the top of the screen carrying everything the rail carries.
 *
 * Under 900px the rail is display:none (shell.css), which until now meant a
 * phone had no way to switch project, reach Usage or Account, flip Simple and
 * Advanced, or Submit Feedback. This panel is not a second navigation: it
 * mounts the rail's own components (ProjectSwitcher, EnvironmentSwitch,
 * ProjectNav, AccountMenu), so a destination added to the rail appears here
 * with no edit to this file.
 *
 * WHY IT MOUNTS ONLY WHILE OPEN. Those components carry data-tour anchors, and
 * the tour resolves an anchor with the first match in the document. The rail
 * is first and reports a zero box on a phone, which the tour already reads as
 * "not on this screen". A second, permanently mounted copy would be a second
 * candidate, so the copy exists only while somebody is looking at it.
 *
 * Closing is animated too, so the panel stays mounted for one slide after the
 * close is asked for (CLOSE_MS, matched to --t-state in mobile-menu.css).
 * Reduced motion is handled by base.css's blanket rule.
 *
 * The focus contract is the Dialog primitive's: focus moves into the panel,
 * Escape and the scrim close it, and focus goes back to the hamburger.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { Wordmark } from '../brand/Wordmark';
import { AccountMenu } from './AccountMenu';
import { EnvironmentSwitch } from './EnvironmentSwitch';
import { ProjectNav } from './ProjectNav';
import { ProjectSwitcher } from './ProjectSwitcher';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/mobile-menu.css';

const CLOSE_MS = 250;

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type Phase = 'closed' | 'open' | 'closing';

interface MobileMenuProps {
  slug: string | null;
}

export function MobileMenu({ slug }: MobileMenuProps) {
  const [phase, setPhase] = useState<Phase>('closed');
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openedAt = useRef(0);

  const phaseRef = useRef<Phase>('closed');
  phaseRef.current = phase;

  const close = useCallback((method: string) => {
    if (phaseRef.current !== 'open') return;
    captureEvent('mobile_menu_closed', {
      close_method: method,
      duration_ms: Date.now() - openedAt.current,
    });
    setPhase('closing');
  }, []);

  function toggle() {
    if (phase === 'open') {
      close('hamburger');
      return;
    }
    openedAt.current = Date.now();
    captureEvent('mobile_menu_opened', { screen_path: location.pathname });
    setPhase('open');
  }

  // Picking a destination is the most common way out, and it is a route
  // change rather than a click this component sees.
  useEffect(() => {
    close('navigated');
  }, [location.pathname, close]);

  useEffect(() => {
    if (phase !== 'closing') return;
    const timer = window.setTimeout(() => {
      setPhase('closed');
      buttonRef.current?.focus();
    }, CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'open') return;
    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panelRef.current)?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [phase]);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const expanded = phase === 'open';

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className="mmenu__toggle"
        aria-label={expanded ? t('Close menu') : t('Open menu')}
        aria-expanded={expanded}
        aria-controls="mobile-menu"
        onClick={toggle}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" focusable="false" aria-hidden="true">
          {expanded ? (
            <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          ) : (
            <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {phase !== 'closed' ? (
        <div className="mmenu" data-phase={phase}>
          <div className="mmenu__scrim" role="presentation" onClick={() => close('scrim')} />
          <div
            className="mmenu__panel"
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label={t('Menu')}
            ref={panelRef}
            tabIndex={-1}
            onKeyDown={onKeyDown}
          >
            <div className="mmenu__head">
              <Link to="/projects" aria-label={t('All projects')}>
                <Wordmark />
              </Link>
              <button
                type="button"
                className="mmenu__toggle"
                aria-label={t('Close menu')}
                onClick={() => close('close_button')}
              >
                <svg viewBox="0 0 16 16" width="16" height="16" focusable="false" aria-hidden="true">
                  <path
                    d="M3.5 3.5l9 9M12.5 3.5l-9 9"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <ProjectSwitcher slug={slug} />
            <EnvironmentSwitch slug={slug} />
            <ProjectNav slug={slug} />
            <AccountMenu />
          </div>
        </div>
      ) : null}
    </>
  );
}
